import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Sparkles, Mail, Send, Copy, Loader2, RefreshCw, FileText
} from "lucide-react";

const MarketingTab = ({
  businesses,
  users,
  crmLeads,
  emailTemplates,
  emailCampaigns,
  onGenerateContent,
  onSendEmail,
  onSaveTemplate,
  onLoadTemplate,
  generating,
  sendingEmail
}) => {
  const [marketingTab, setMarketingTab] = useState("content");
  const [contentType, setContentType] = useState("social_post");
  const [contentPlatform, setContentPlatform] = useState("instagram");
  const [contentTone, setContentTone] = useState("luxurious");
  const [contentContext, setContentContext] = useState("");
  const [generatedContent, setGeneratedContent] = useState("");
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailRecipients, setEmailRecipients] = useState("all_members");

  const handleGenerateContent = async () => {
    const context = {};

    if (contentType === "social_post") {
      context.topic = contentContext || "membership benefits and partner discounts";
    } else if (contentType === "business_spotlight" && selectedBusiness) {
      context.business_name = selectedBusiness.name;
      context.category = selectedBusiness.category;
      context.description = selectedBusiness.description;
      context.discount = selectedBusiness.discount_level;
    } else if (contentType === "email" || contentType === "promo") {
      context.purpose = contentContext || "general promotion";
      context.promotion = contentContext;
    } else if (contentType === "welcome_email" || contentType === "renewal_reminder") {
      context.membership_type = "monthly";
      context.days_until_expiry = 7;
    }

    const content = await onGenerateContent({
      type: contentType,
      platform: contentPlatform,
      tone: contentTone,
      context
    });

    if (content) {
      setGeneratedContent(content);
    }
  };

  const handleCopyContent = () => {
    navigator.clipboard.writeText(generatedContent);
  };

  const handleUseAsEmail = () => {
    const lines = generatedContent.split('\n');
    let subject = "";
    let body = "";

    for (let i = 0; i < lines.length; i++) {
      if (lines[i].toLowerCase().includes("subject:")) {
        subject = lines[i].replace(/subject:/i, "").trim();
      }
    }

    body = lines.filter(l => !l.toLowerCase().includes("subject:")).join('\n').trim();

    setEmailSubject(subject || "Advanta Plus Update");
    setEmailBody(body || generatedContent);
    setMarketingTab("email");
  };

  const handleSendEmail = async () => {
    if (!emailSubject || !emailBody) return;
    const success = await onSendEmail({
      subject: emailSubject,
      body: emailBody,
      recipients: [emailRecipients]
    });
    if (success) {
      setEmailSubject("");
      setEmailBody("");
    }
  };

  const handleSaveTemplate = async () => {
    if (!emailSubject || !emailBody) return;
    await onSaveTemplate({
      name: emailSubject.substring(0, 50),
      subject: emailSubject,
      body: emailBody,
      type: "custom"
    });
  };

  const handleLoadTemplate = (template) => {
    setEmailSubject(template.subject);
    setEmailBody(template.body);
    onLoadTemplate(template);
  };

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-4">
        {[
          { id: "content", label: "AI Content Generator", icon: Sparkles },
          { id: "email", label: "Email Composer", icon: Mail },
          { id: "campaigns", label: "Campaign History", icon: Send }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setMarketingTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${marketingTab === tab.id
              ? 'bg-[#D4AF37] text-[#020617]'
              : 'bg-white/5 text-white/60 hover:bg-white/10'
              }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* AI Content Generator */}
      {marketingTab === "content" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Controls */}
          <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 overflow-hidden">
            <div className="p-4 border-b border-white/5">
              <h3 className="font-semibold text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                AI Content Generator
              </h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <Label className="text-white/70">Content Type</Label>
                <Select value={contentType} onValueChange={setContentType}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="social_post">Social Media Post</SelectItem>
                    <SelectItem value="email">Marketing Email</SelectItem>
                    <SelectItem value="promo">Promotional Copy</SelectItem>
                    <SelectItem value="business_spotlight">Business Spotlight</SelectItem>
                    <SelectItem value="welcome_email">Welcome Email</SelectItem>
                    <SelectItem value="renewal_reminder">Renewal Reminder</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {contentType === "social_post" && (
                <div>
                  <Label className="text-white/70">Platform</Label>
                  <Select value={contentPlatform} onValueChange={setContentPlatform}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="facebook">Facebook</SelectItem>
                      <SelectItem value="twitter">Twitter/X</SelectItem>
                      <SelectItem value="linkedin">LinkedIn</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label className="text-white/70">Tone</Label>
                <Select value={contentTone} onValueChange={setContentTone}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="luxurious">Luxurious & Elegant</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="friendly">Friendly & Casual</SelectItem>
                    <SelectItem value="urgent">Urgent & Exciting</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {contentType === "business_spotlight" && (
                <div>
                  <Label className="text-white/70">Select Business</Label>
                  <Select
                    value={selectedBusiness?.id || ""}
                    onValueChange={(id) => setSelectedBusiness(businesses.find(b => b.id === id))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                      <SelectValue placeholder="Choose a business..." />
                    </SelectTrigger>
                    <SelectContent>
                      {businesses.map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {(contentType === "social_post" || contentType === "email" || contentType === "promo") && (
                <div>
                  <Label className="text-white/70">Topic / Context</Label>
                  <Input
                    value={contentContext}
                    onChange={(e) => setContentContext(e.target.value)}
                    className="bg-white/5 border-white/10 text-white mt-1"
                    placeholder="e.g., Summer promotion, New partner restaurant, Membership benefits..."
                  />
                </div>
              )}

              <Button
                onClick={handleGenerateContent}
                disabled={generating}
                className="w-full btn-gold py-3"
                data-testid="generate-content-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Content
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Generated Content */}
          <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 overflow-hidden">
            <div className="p-4 border-b border-white/5 flex items-center justify-between">
              <h3 className="font-semibold text-white">Generated Content</h3>
              {generatedContent && (
                <div className="flex gap-2">
                  <Button
                    onClick={handleCopyContent}
                    variant="ghost"
                    size="sm"
                    className="text-white/60 hover:text-white"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                  {(contentType === "email" || contentType === "welcome_email" || contentType === "renewal_reminder") && (
                    <Button
                      onClick={handleUseAsEmail}
                      variant="ghost"
                      size="sm"
                      className="text-[#D4AF37] hover:text-[#F5DEB3]"
                    >
                      <Mail className="w-4 h-4 mr-1" />
                      Use as Email
                    </Button>
                  )}
                  <Button
                    onClick={() => setGeneratedContent("")}
                    variant="ghost"
                    size="sm"
                    className="text-white/60 hover:text-white"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            <div className="p-4">
              {generatedContent ? (
                <div className="bg-white/5 rounded-lg p-4 whitespace-pre-wrap text-white/90 text-sm max-h-[500px] overflow-y-auto">
                  {generatedContent}
                </div>
              ) : (
                <div className="text-center py-12 text-white/40">
                  <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Generated content will appear here</p>
                  <p className="text-xs mt-1">Select options and click Generate</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email Composer */}
      {marketingTab === "email" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Composer */}
          <div className="lg:col-span-2 bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 overflow-hidden">
            <div className="p-4 border-b border-white/5">
              <h3 className="font-semibold text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#D4AF37]" />
                Email Composer
              </h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <Label className="text-white/70">Recipients</Label>
                <Select value={emailRecipients} onValueChange={setEmailRecipients}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_members">All Members ({users.filter(u => u.role === 'member').length})</SelectItem>
                    <SelectItem value="expiring_soon">Expiring Soon (7 days)</SelectItem>
                    <SelectItem value="leads">CRM Leads ({crmLeads.length})</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/70">Subject Line</Label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  placeholder="Your email subject..."
                />
                <p className="text-xs text-white/40 mt-1">Use {"{{name}}"} for personalization</p>
              </div>
              <div>
                <Label className="text-white/70">Email Body</Label>
                <textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 text-white mt-1 rounded-md p-3 min-h-[250px] text-sm"
                  placeholder="Write your email content here...&#10;&#10;Use {{name}} for personalization."
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveTemplate}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/5"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Save Template
                </Button>
                <Button
                  onClick={handleSendEmail}
                  disabled={sendingEmail || !emailSubject || !emailBody}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                >
                  {sendingEmail ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Campaign
                </Button>
              </div>
            </div>
          </div>

          {/* Templates */}
          <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 overflow-hidden">
            <div className="p-4 border-b border-white/5">
              <h3 className="font-semibold text-white">Saved Templates</h3>
            </div>
            <div className="p-4 space-y-2 max-h-[400px] overflow-y-auto">
              {emailTemplates.length > 0 ? (
                emailTemplates.map(template => (
                  <button
                    key={template.id}
                    onClick={() => handleLoadTemplate(template)}
                    className="w-full text-left bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors"
                  >
                    <p className="text-white font-medium text-sm truncate">{template.name}</p>
                    <p className="text-white/40 text-xs truncate mt-1">{template.subject}</p>
                  </button>
                ))
              ) : (
                <p className="text-white/40 text-sm text-center py-4">No templates saved yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Campaign History */}
      {marketingTab === "campaigns" && (
        <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 overflow-hidden">
          <div className="p-4 border-b border-white/5">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <Send className="w-5 h-5 text-[#D4AF37]" />
              Campaign History
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left p-4 text-white/50 text-sm font-medium">Date</th>
                  <th className="text-left p-4 text-white/50 text-sm font-medium">Subject</th>
                  <th className="text-left p-4 text-white/50 text-sm font-medium">Sent</th>
                  <th className="text-left p-4 text-white/50 text-sm font-medium">Failed</th>
                  <th className="text-left p-4 text-white/50 text-sm font-medium">Sent By</th>
                </tr>
              </thead>
              <tbody>
                {emailCampaigns.map((campaign) => (
                  <tr key={campaign.id} className="border-t border-white/5">
                    <td className="p-4 text-white/50 text-sm">
                      {campaign.created_at ? new Date(campaign.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="p-4 text-white">{campaign.subject}</td>
                    <td className="p-4">
                      <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs font-medium">
                        {campaign.sent_count}
                      </span>
                    </td>
                    <td className="p-4">
                      {campaign.failed_count > 0 ? (
                        <span className="bg-red-500/20 text-red-400 px-2 py-1 rounded text-xs font-medium">
                          {campaign.failed_count}
                        </span>
                      ) : (
                        <span className="text-white/40">0</span>
                      )}
                    </td>
                    <td className="p-4 text-white/50 text-sm">{campaign.sent_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {emailCampaigns.length === 0 && (
              <p className="p-8 text-center text-white/40">No campaigns sent yet</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingTab;
